import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 2: Ubuntu Infrastructure Automation & STIG Compliance
 * - Central management VPC with web, mirrors, Landscape, STIG repo, DB1
 * - Peered secondary DB VPC
 * - VPN down to org desktops pulling updates/scripts
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 240,
    textAlign: "center",
  },
  subsection: {
    background: "#f8fafc",
    border: "2px dashed #94a3b8",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    color: "#334155",
    width: 180,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 150,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 150,
    textAlign: "center",
  },
  security: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 150,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#05333b",
    width: 140,
    textAlign: "center",
  },
  desktop: {
    background: "#fff7ed",
    border: "2px solid #fb923c",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#7c2d12",
    width: 120,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 220,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project2FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background containers
      {
        id: "bg-vpc1",
        position: { x: 40, y: 30 },
        data: { label: "" },
        style: {
          width: 640,
          height: 350,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-vpc1-subnet1",
        position: { x: 70, y: 85 },
        data: { label: "" },
        style: {
          width: 430,
          height: 155,
          background: "transparent",
          border: "2px dashed #60a5fa",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-vpc1-subnet2",
        position: { x: 70, y: 240 },
        data: { label: "" },
        style: {
          width: 260,
          height: 110,
          background: "transparent",
          border: "2px dashed #60a5fa",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-vpc3",
        position: { x: 720, y: 50 },
        data: { label: "" },
        style: {
          width: 360,
          height: 240,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-org",
        position: { x: 200, y: 430 },
        data: { label: "" },
        style: {
          width: 720,
          height: 220,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // VPC 1 labels
      {
        id: "sec-vpc1",
        position: { x: 60, y: 40 },
        data: { label: "VPC 1 - CENTRAL MANAGEMENT" },
        style: nodeStyle.section,
      },
      {
        id: "subnet1",
        position: { x: 80, y: 95 },
        data: { label: "SUBNET 1" },
        style: nodeStyle.subsection,
      },
      {
        id: "subnet2",
        position: { x: 80, y: 250 },
        data: { label: "SUBNET 2" },
        style: nodeStyle.subsection,
      },

      // VPC 1 services
      {
        id: "web",
        position: { x: 90, y: 140 },
        data: { label: "Web Server" },
        style: nodeStyle.compute,
      },
      {
        id: "mirrors",
        position: { x: 260, y: 140 },
        data: { label: "Ubuntu Mirrors" },
        style: nodeStyle.compute,
      },
      {
        id: "landscape",
        position: { x: 170, y: 205 },
        data: { label: "K8s Landscape" },
        style: nodeStyle.compute,
      },
      {
        id: "stig",
        position: { x: 350, y: 205 },
        data: { label: "STIG Repo" },
        style: nodeStyle.security,
      },
      {
        id: "db1",
        position: { x: 170, y: 290 },
        data: { label: "Database 1" },
        style: nodeStyle.data,
      },

      // VPC 3 labels and services
      {
        id: "sec-vpc3",
        position: { x: 740, y: 60 },
        data: { label: "VPC 3 - SECONDARY DB" },
        style: { ...nodeStyle.section, width: 260 },
      },
      {
        id: "subnet3",
        position: { x: 760, y: 105 },
        data: { label: "SUBNET 1" },
        style: nodeStyle.subsection,
      },
      {
        id: "db2",
        position: { x: 785, y: 150 },
        data: { label: "Database 2" },
        style: nodeStyle.data,
      },

      // VPN node
      {
        id: "vpn",
        position: { x: 540, y: 360 },
        data: { label: "VPN Gateway" },
        style: nodeStyle.net,
      },

      // Organization desktops
      {
        id: "sec-org",
        position: { x: 220, y: 440 },
        data: { label: "ORGANIZATION - UBUNTU DESKTOPS" },
        style: { ...nodeStyle.section, width: 420, borderColor: "#fb923c" },
      },
      {
        id: "d1",
        position: { x: 240, y: 500 },
        data: { label: "Desktop 1" },
        style: nodeStyle.desktop,
      },
      {
        id: "d2",
        position: { x: 370, y: 500 },
        data: { label: "Desktop 2" },
        style: nodeStyle.desktop,
      },
      {
        id: "d3",
        position: { x: 500, y: 500 },
        data: { label: "Desktop 3" },
        style: nodeStyle.desktop,
      },
      {
        id: "dn",
        position: { x: 630, y: 500 },
        data: { label: "Desktop ..." },
        style: nodeStyle.desktop,
      },
      {
        id: "pull",
        position: { x: 360, y: 585 },
        data: { label: "Pull Updates & Scripts" },
        style: nodeStyle.note,
      },
    ],
    []
  );

  const edges = useMemo(() => {
    const purple = { stroke: "#7c3aed" };
    const orange = { stroke: "#f59e0b" };
    const teal = { stroke: "#0ea5e9" };

    return [
      // Subnet 1 -> Subnet 2 data access
      mkEdge("e-web-db1", "web", "db1", "store/update", {
        style: { stroke: "#0ea5e9", strokeWidth: 4 },
      }),
      mkEdge("e-mirrors-db1", "mirrors", "db1", "sync metadata", {
        style: { stroke: "#0ea5e9", strokeWidth: 4 },
      }),
      mkEdge("e-landscape-db1", "landscape", "db1", "state", {
        style: { stroke: "#0ea5e9", strokeWidth: 4 },
      }),
      mkEdge("e-stig-db1", "stig", "db1", "compliance data", {
        style: { stroke: "#0ea5e9", strokeWidth: 4 },
      }),

      // Peering between VPCs
      mkEdge("e-vpc1-vpc3", "db1", "db2", "VPC Peering", {
        animated: true,
        style: { ...purple, strokeWidth: 5, strokeDasharray: "10 8" },
      }),

      // VPN to organization
      mkEdge("e-vpc3-vpn", "db2", "vpn", "VPN", {
        animated: true,
        style: { ...orange, strokeWidth: 5, strokeDasharray: "12 8" },
      }),
      mkEdge("e-vpn-org", "vpn", "pull", "Secure Access", {
        animated: true,
        style: { ...orange, strokeWidth: 5, strokeDasharray: "12 8" },
      }),

      // Updates and scripts to desktops
      mkEdge("e-landscape-pull", "landscape", "pull", "Publish Updates", {
        style: { ...teal, strokeWidth: 4 },
      }),
      mkEdge("e-pull-d1", "pull", "d1", "Pull", { style: { ...teal, strokeWidth: 3 } }),
      mkEdge("e-pull-d2", "pull", "d2", "Pull", { style: { ...teal, strokeWidth: 3 } }),
      mkEdge("e-pull-d3", "pull", "d3", "Pull", { style: { ...teal, strokeWidth: 3 } }),
      mkEdge("e-pull-dn", "pull", "dn", "Pull", { style: { ...teal, strokeWidth: 3 } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "740px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
